import { PlusCircleOutlined } from '@ant-design/icons'
import type { SiteDwellResp } from '@components/DriverTracking/Dwell/api/schema'
import { useDwellAPI } from '@components/DriverTracking/Dwell/api/useDwellAPI'
import { getColumnDefs } from '@components/DriverTracking/Dwell/components/DwellSiteGrid/columnDefs'
import { parseTimeStringToMinutes } from '@components/_SiteWide/Form/HourMinuteInput'
import { DownloadButton } from '@components/_SiteWide/Grid/ActionButtons/DownloadButton'
import { GraviButton, GraviGrid, Horizontal, NotificationMessage } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { getShowNetworkTimezone } from '@utils/general'
import { compareStoreNumbers, gridViewManager } from '@utils/grid'
import type { Moment } from 'moment-timezone'
import { useMemo, useRef, useState } from 'react'

const STORAGE_KEY = 'dwell-site-config'

// If user has network timezone enabled, convert expiry time to network timezone before sending request
// Otherwise, the expiry time gets converted to the user's local timezone in the response
export function convertExpiryTimeToLocal(time: Moment, timeZone: Intl.DateTimeFormatOptions['timeZone']) {
  const showNetworkTime = getShowNetworkTimezone({ timeZone })
  if (!showNetworkTime) return time
  const expiryDate = time.clone().tz(timeZone)
  expiryDate.set({
    hour: time.hour(),
    minute: time.minute(),
  })
  return expiryDate
}

const validatePayload = (payload) => {
  const hasInvalidFields = payload.some((item) => item.site_id == null || item.product_group == null)

  if (hasInvalidFields) {
    NotificationMessage('Please fill out all required fields', 'Site and Product Group are required.', true)
    return true
  }
}

export function DwellSiteGrid() {
  const gridAPIRef = useRef(null)
  const dirtyRef = useRef<any>(null)
  const { updateSetting } = useUserSettings()

  const [hasEdits, setHasEdits] = useState(false)

  const { useSiteDwellOverview, useSiteDwellUpsert, useSiteDwellDelete, useSiteDwellOverride, useDwellMetadata } =
    useDwellAPI()

  const { data: metadata } = useDwellMetadata()
  const sites = metadata?.sites ?? []
  const product_groups = metadata?.product_groups ?? []

  const createHandleOverride = (params) => async (values) => {
    // Used to preload the form with Cell data to execute the override
    const expiryDate = convertExpiryTimeToLocal(values.date, params.data.override_expiry_local)

    const payload = {
      site_id: params.data.site_id,
      product_group: params.data.product_group,
      dwell_override: parseTimeStringToMinutes(values.overrideTime),
      override_expiry: expiryDate.toISOString(),
    }
    const result = await useSiteDwellOverride.mutateAsync(payload)
    gridAPIRef?.current?.applyTransaction({ update: [result] })
  }

  const createHandleDeleteOverride = (params) => async () => {
    // Used to preload the form with Cell data to execute removing the override
    const payload = {
      site_id: params.data.site_id,
      product_group: params.data.product_group,
      dwell_override: null,
      override_expiry: null,
    }
    const result = await useSiteDwellOverride.mutateAsync(payload)
    gridAPIRef?.current?.applyTransaction({ update: [result] })
  }

  const createHandleDelete = (params) => async () => {
    // Used to preload the form with Cell data to delete the row
    const payload = {
      site_id: params.data.site_id,
      product_group: params.data.product_group,
    }
    const rowId = `${params.data.site_id}-${params.data.product_group}`
    await useSiteDwellDelete.mutateAsync(payload)
    gridAPIRef?.current?.applyTransaction({ remove: [{ id: rowId }] })
  }

  const columnDefs = getColumnDefs(
    sites?.map(({ site_number }) => site_number).sort(compareStoreNumbers),
    product_groups,
    createHandleOverride,
    createHandleDeleteOverride,
    createHandleDelete,
    hasEdits
  )

  const siteLookup = useMemo(() => {
    return sites.reduce((lookup, { site_id, site_number }) => {
      lookup[site_number] = site_id
      return lookup
    }, {})
  }, [sites])

  const {
    data,
    isPending: isLoadingOverview,
    isFetching: isFetchingOverview,
  } = useSiteDwellOverview() as {
    data: SiteDwellResp[] | undefined
    isPending: boolean
    isFetching: boolean
  }

  const rowData: SiteDwellResp[] = useMemo(() => {
    return structuredClone(data || [])
  }, [data])

  const handleAddProductGroup = () => {
    dirtyRef.current?.addDirtyRow({
      site_number: '',
      set_dwell: 30,
    })
    setHasEdits(true)
  }

  const handleCellEdit = (params) => {
    if (!params.valueChanged) return
    setHasEdits(true)
  }

  const handleSave = ({ dirtyChanges }) => {
    const payload = dirtyChanges.map((change) => ({
      site_id: siteLookup[change.site_number],
      product_group: change.product_group,
      set_dwell: change.set_dwell,
      payroll_override: change.payroll_override,
    }))
    if (validatePayload(payload)) return false
    useSiteDwellUpsert.mutate(payload)
    dirtyRef?.current?.setDirtyChanges([])
    setHasEdits(false)
    return true
  }

  const handleDiscard = () => {
    setHasEdits(false)
    const filteredData = gridAPIRef.current
      .getRenderedNodes()
      .map((node) => node.data)
      .filter((row) => !row.$inserted)
    gridAPIRef.current.setRowData(filteredData)
  }

  const agPropOverrides = useMemo(() => {
    return {
      getRowId: (row) => row.data?.id,
      groupDisplayType: 'multipleColumns',
      suppressAggFuncInHeader: true,
      onCellEditingStopped: handleCellEdit,
    }
  }, [])

  const controlBarProps = useMemo(() => {
    return {
      title: 'Sites',
      actionButtons: (
        <Horizontal verticalCenter style={{ gap: '1em' }}>
          <DownloadButton fileName='Site Dwell Times' gridAPIRef={gridAPIRef} />
          <GraviButton
            color='primary'
            icon={<PlusCircleOutlined />}
            buttonText='Product Grouping'
            onClick={handleAddProductGroup}
          />
        </Horizontal>
      ),
    }
  }, [])

  return (
    <GraviGrid
      columnDefs={columnDefs}
      agPropOverrides={agPropOverrides}
      controlBarProps={controlBarProps}
      primaryKey='id'
      storageKey={STORAGE_KEY}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      loading={isLoadingOverview || isFetchingOverview}
      rowData={rowData}
      bulkDrawerTitle='Bulk Edit Dwell Times'
      externalRef={gridAPIRef}
      isDirtyEdit
      dirtyChangesRef={dirtyRef}
      onDirtyChangeSave={handleSave}
      onDirtyChangeDiscard={handleDiscard}
      hideSaveDisplay
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}
